import { Edit2, X } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { OnlineSchedule } from "./OnlineSchedule";
import { OfflineSchedule } from "./OfflineSchedule";
import { IASchedule } from "./IASchedule";
import { AddNewPreference } from "./preference-form/AddNewPreference";
import { DoctorService } from "../../services/api/doctor.service";
import { useAuth } from "../../context/AuthContext";

export default function PreferencesModal({ doctorId }: { doctorId: string }) {
  const { user } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const [isEditable, setIsEditable] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Online Schedule");
  const [showAddNewSchedule, setShowAddNewSchedule] = useState({
    status: false,
    type: "",
  });
  const tabs = [
    "Online Schedule",
    "Offline Schedule",
    "IA Schedule",
  ];
  const [fetchLoading, setFetchLoading] = useState(true);
  const [prefetchPreference, setPrefetchPreference] = useState(false);

  const updateExistingDoctorPreferences = async (formData: any) => {
    try {
      const response = await DoctorService.updateExistingDoctorPreferences(
        formData
      );

      if (response && response.success) {
        toast.success(
          response.message || "Doctor preferences updated successfully"
        );
        return true;
      } else {
        toast.error(
          response?.message || "Error while updating doctor preferences"
        );
        return false;
      }
    } catch (error) {
      console.error("Error updating Doctor Preferences:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update doctor preferences"
      );
      return false;
    }
  };

  const handleToggleNewScheduleType = (type: string) => {
    if (!isAdmin) return;
    setShowAddNewSchedule({
      status: true,
      type: type,
    });
  };

  return (
    <section className="w-full overflow-y-auto hiddenScrollbar my-5">
      {/* White box */}
      <div className="bg-white rounded-md">
        {/* Box header */}
        <div className="w-full rounded-t-md p-4 ">
          {/* Heading and edit button */}
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-base ">
              Preferences & Availability
            </h2>
            {isAdmin && (
              <button
                onClick={() => setIsEditable(!isEditable)}
                className="disabled:pointer-events-none disabled:text-[#9e9e9e] text-[#199C4D] flex items-center justify-center gap-2 font-semibold text-sm cursor-pointer"
              >
                {isEditable ? (
                  <X className="h-4 w-4 text-[#199C4D]" />
                ) : (
                  <Edit2 className="h-4 w-4 text-[#199C4D]" />
                )}
                {isEditable ? "Cancel" : "Edit"}
              </button>
            )}
          </div>
        </div>

        {/* Tab buttons box */}
        <div className="w-full bg-[#e4e3e3] flex items-center gap-4 px-4 h-[45px] my-6">
          {tabs.map((item, index) => {
            return (
              <button
                type="button"
                key={item + index}
                className={`border-b-2 h-full px-3 flex items-center ${
                  selectedTab === item
                    ? "text-[#771D51] border-b-[#771D51] "
                    : "border-b-[#e4e3e3]"
                }`}
                onClick={() => {
                  setIsEditable(false);
                  setSelectedTab(item);
                }}
              >
                <span>{item}</span>
              </button>
            );
          })}
        </div>

        {/* Tabs Container */}
        <div className="w-full min-h-[200px] max-h-[550px] overflow-y-auto hiddenScrollbar rounded-b-md p-4">
          {selectedTab === "Online Schedule" && (
            <OnlineSchedule
              isEditable={isAdmin && isEditable}
              doctorId={doctorId}
              onSuccess={() => setIsEditable(false)}
              updateDoctorPreference={updateExistingDoctorPreferences}
              handleToggleNewScheduleType={handleToggleNewScheduleType}
              prefetchPreference={prefetchPreference}
            />
          )}
          {selectedTab === "Offline Schedule" && (
            <OfflineSchedule
              isEditable={isAdmin && isEditable}
              doctorId={doctorId}
              onSuccess={() => setIsEditable(false)}
              updateDoctorPreference={updateExistingDoctorPreferences}
              handleToggleNewScheduleType={handleToggleNewScheduleType}
              prefetchPreference={prefetchPreference}
            />
          )}
          {selectedTab === "IA Schedule" && (
            <IASchedule
              isEditable={isAdmin && isEditable}
              doctorId={doctorId}
              onSuccess={() => setIsEditable(false)}
              updateDoctorPreference={updateExistingDoctorPreferences}
              handleToggleNewScheduleType={handleToggleNewScheduleType}
              prefetchPreference={prefetchPreference}
            />
          )}

          {isAdmin && showAddNewSchedule.status && (
            <AddNewPreference
              type={showAddNewSchedule.type}
              onClose={() =>
                setShowAddNewSchedule({
                  status: false,
                  type: "",
                })
              }
              doctorId={doctorId}
              onSuccess={() => setPrefetchPreference(true)}
            />
          )}
        </div>
      </div>
    </section>
  );
}
